import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { TPhaseColors, TProcedureLanguageColors } from './types';
import { focusRing, focusRingOutline } from '../../Themes/base/focusRing';
import { borderColor, borderStyle, borderWidth } from '../../Utilities/utils/Theming';

export const applicationPhaseStyle = (phase: string, theme: typeof Theme) => {
	const { label, ...rest } = getPhaseDetails(phase, theme);

	return {
		display: 'flex',
		alignItems: 'center',
		fontWeight: 'normal',
		borderTopLeftRadius: theme.applicationNumber.phaseBorderTopLeftRadius,
		borderBottomLeftRadius: theme.applicationNumber.phaseBorderBottomLeftRadius,
		paddingTop: theme.applicationNumber.phaseTopPadding,
		paddingBottom: theme.applicationNumber.phaseBottomPadding,
		paddingLeft: theme.applicationNumber.phaseLeftPadding,
		paddingRight: theme.applicationNumber.phaseRightPadding,
		fontSize: theme.applicationNumber.fontSize,
		fontFamily: 'var(--epods--font-secondary)',
		lineHeight: theme.applicationNumber.lineHeight,
		':focus': focusRing,
		...rest,
	};
};

export const applicationLanguageStyle = (procedureCode: string, theme: typeof Theme) => {
	const languageStyle = getApplicationNumbeProcedureAndLanguageColors(procedureCode, theme);

	return {
		border: 'none',
		display: 'flex',
		alignItems: 'center',
		fontWeight: 'normal',
		borderTopRightRadius: theme.applicationNumber.languageBorderTopRightRadius,
		borderBottomRightRadius: theme.applicationNumber.languageBorderBottomRightRadius,
		paddingTop: theme.applicationNumber.languageTopPadding,
		paddingBottom: theme.applicationNumber.languageBottomPadding,
		paddingLeft: theme.applicationNumber.languageLeftPadding,
		paddingRight: theme.applicationNumber.languageRightPadding,
		fontSize: theme.applicationNumber.fontSize,
		fontFamily: 'var(--epods--font-secondary)',
		color: languageStyle.language.color,
		backgroundColor: languageStyle.language.backgroundColor,
		lineHeight: theme.applicationNumber.lineHeight,
		':focus': focusRing,
	};
};

export const applicationProcedureStyle = (procedureCode: string, theme: typeof Theme, phase?: string) => {
	const procedureStyle = getApplicationNumbeProcedureAndLanguageColors(procedureCode, theme);

	const borderLeftRadius = {
		borderTopLeftRadius: !phase ? theme.applicationNumber.phaseBorderTopLeftRadius : '0px',
		borderBottomLeftRadius: !phase ? theme.applicationNumber.phaseBorderBottomLeftRadius : '0px',
	};

	return {
		display: 'flex',
		alignItems: 'center',
		fontWeight: 'normal',
		paddingTop: theme.applicationNumber.procedureTopPadding,
		paddingBottom: theme.applicationNumber.procedureBottomPadding,
		paddingLeft: theme.applicationNumber.procedureLeftPadding,
		paddingRight: theme.applicationNumber.procedureRightPadding,

		borderTopColor: procedureStyle.procedure.borderTopColor,
		borderRightColor: procedureStyle.procedure.borderRightColor,
		borderBottomColor: procedureStyle.procedure.borderBottomColor,
		borderLeftColor: procedureStyle.procedure.borderLeftColor,
		...borderStyle('solid'),
		...borderWidth(theme.applicationNumber.procedureBorderWidth),

		fontSize: theme.applicationNumber.fontSize,
		fontFamily: 'var(--epods--font-secondary)',

		color: procedureStyle.procedure.color,
		backgroundColor: procedureStyle.procedure.backgroundColor,
		lineHeight: theme.applicationNumber.lineHeight,
		...borderLeftRadius,
		':focus': focusRing,
	};
};

export const applicationNumberDossierLines = (procedureCode: string, theme: typeof Theme) => {
	const procedureStyle = getApplicationNumbeProcedureAndLanguageColors(procedureCode, theme);

	return {
		position: 'relative',
		width: '6px',
		height: '17px',
		borderBottomWidth: '2px',
		borderBottomStyle: 'solid',
		borderBottomColor: procedureStyle.procedure.borderBottomColor,
		borderLeftWidth: '2px',
		borderLeftStyle: 'solid',
		borderLeftColor: procedureStyle.procedure.borderLeftColor,
		marginLeft: '32px',
	};
};

export const applicationNumberDossierNumber = (theme: typeof Theme) => ({
	marginTop: '2px',
	marginLeft: '2px',
	paddingLeft: '2px',
	fontSize: theme.applicationNumber.fontSize,
	fontFamily: 'var(--epods--font-secondary)',
	lineHeight: theme.applicationNumber.lineHeight,
	border: 'none',
	backgroundColor: 'transparent',
	color: 'var(--epods--color-text-primary)',
});

export const applicationNumberPublicationNumber = (theme: typeof Theme) => ({
	marginTop: '2px',
	marginLeft: '2px',
	paddingLeft: '2px',
	fontSize: theme.applicationNumber.fontSize,
	fontFamily: 'var(--epods--font-secondary)',
	lineHeight: theme.applicationNumber.lineHeight,
	border: 'none',
	backgroundColor: 'transparent',
	color: 'var(--epods--color-text-primary)',
});

const noneBorders = {
	borderTopWidth: '0px',
	borderRightWidth: '0px',
	borderBottomWidth: '0px',
	borderLeftWidth: '0px',
	borderTopStyle: 'none',
	borderRightStyle: 'none',
	borderBottomStyle: 'none',
	borderLeftStyle: 'none',
	borderTopColor: 'transparent',
	borderRightColor: 'transparent',
	borderBottomColor: 'transparent',
	borderLeftColor: 'transparent',
};

const cssDefaultPhaseStyling = (theme: typeof Theme) => ({
	code: 'D',
	label: '',
	color: 'var(--epods--color-bg)',
	backgroundColor: 'var(--epods--color-bg)',
	borderTopWidth: '1px',
	borderRightWidth: '0px',
	borderBottomWidth: '1px',
	borderLeftWidth: '1px',
	borderTopStyle: 'solid',
	borderRightStyle: 'none',
	borderBottomStyle: 'solid',
	borderLeftStyle: 'solid',
	borderTopColor: 'var(--epods--color-text-primary)',
	borderRightColor: 'transparent',
	borderBottomColor: 'var(--epods--color-text-primary)',
	borderLeftColor: 'var(--epods--color-text-primary)',
});

export const cssApplicationNumberContainer = (theme: typeof Theme) => ({
	borderTopWidth: '0px',
	borderRightWidth: '0px',
	borderBottomWidth: '0px',
	borderLeftWidth: '0px',
	borderTopStyle: 'solid',
	borderRightStyle: 'solid',
	borderBottomStyle: 'solid',
	borderLeftStyle: 'solid',
	borderTopColor: 'var(--epods--color-grey-0)',
	borderRightColor: 'var(--epods--color-grey-0)',
	borderBottomColor: 'var(--epods--color-grey-0)',
	borderLeftColor: 'var(--epods--color-grey-0)',
	backgroundColor: 'var(--epods--color-grey-0)',
	display: 'flex',
	borderRadius: theme.applicationNumber.phaseBorderTopLeftRadius,
});

export const getPhaseDetails = (phaseKey: string = 'default', theme: typeof Theme) => {
	const phase: TPhaseColors = {
		search: {
			code: 'S',
			label: 'Search',
			color: theme.applicationNumber.phaseSearchColor,
			backgroundColor: theme.applicationNumber.phaseSearchBackgroundColor,
			...noneBorders,
		},
		examination: {
			code: 'E',
			label: 'Examination',
			color: theme.applicationNumber.phaseExaminationColor,
			backgroundColor: theme.applicationNumber.phaseExaminationBackgroundColor,
			...noneBorders,
		},
		opposition: {
			code: 'O',
			label: 'Opposition',
			color: theme.applicationNumber.phaseOppositionColor,
			backgroundColor: theme.applicationNumber.phaseOppositionBackgroundColor,
			...noneBorders,
		},
		appeal: {
			code: 'A',
			label: 'Appeal',
			color: theme.applicationNumber.phaseAppealColor,
			backgroundColor: theme.applicationNumber.phaseAppealBackgroundColor,
			...noneBorders,
		},
		'post grand': {
			code: 'P',
			label: 'Post grand',
			color: theme.applicationNumber.phasePostGrandColor,
			backgroundColor: theme.applicationNumber.phasePostGrandBackgroundColor,
			...noneBorders,
		},
		intake: {
			code: 'I',
			label: 'Intake',
			color: theme.applicationNumber.phaseIntakeColor,
			backgroundColor: theme.applicationNumber.phaseIntakeBackgroundColor,
			...noneBorders,
		},
		'receiving section office': {
			code: 'R',
			label: 'Receiving Section Office',
			color: theme.applicationNumber.phaseReceivingSectionOfficeColor,
			backgroundColor: theme.applicationNumber.phaseReceivingSectionOfficeBackgroundColor,
			...noneBorders,
		},
		default: cssDefaultPhaseStyling(theme),
	};
	const key = phaseKey.toLowerCase();
	const phaseColors = {
		label: phase[key].label,
		color: phase[key].color,
		backgroundColor: phase[key].backgroundColor,
		borderTopWidth: phase[key].borderTopWidth,
		borderRightWidth: phase[key].borderRightWidth,
		borderBottomWidth: phase[key].borderBottomWidth,
		borderLeftWidth: phase[key].borderLeftWidth,
		borderTopStyle: phase[key].borderTopStyle,
		borderRightStyle: phase[key].borderRightStyle,
		borderBottomStyle: phase[key].borderBottomStyle,
		borderLeftStyle: phase[key].borderLeftStyle,
		borderTopColor: phase[key].borderTopColor,
		borderRightColor: phase[key].borderRightColor,
		borderBottomColor: phase[key].borderBottomColor,
		borderLeftColor: phase[key].borderLeftColor,
	};

	return phaseColors || cssDefaultPhaseStyling(theme);
};

const getApplicationNumbeProcedureAndLanguageColors = (procedureCode: string = 'DEFAULT', theme: typeof Theme) => {
	const applicationNumberMap: TProcedureLanguageColors = {
		PCT: {
			id: 'PCT',
			label: 'PCT',
			procedure: {
				color: theme.applicationNumber.procedurePCTColor,
				backgroundColor: theme.applicationNumber.procedurePCTBackgroundColor,
				...borderColor(theme.applicationNumber.procedurePCTBorderColor),
			},
			language: {
				color: theme.applicationNumber.languagePCTColor,
				backgroundColor: theme.applicationNumber.languagePCTBackgroundColor,
			},
		},
		EP: {
			id: 'EP',
			label: 'EP',
			procedure: {
				color: theme.applicationNumber.procedureEPColor,
				backgroundColor: theme.applicationNumber.procedureEPBackgroundColor,
				...borderColor(theme.applicationNumber.procedureEPBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageEPColor,
				backgroundColor: theme.applicationNumber.languageEPBackgroundColor,
			},
		},
		FA: {
			id: 'FA',
			label: 'National Search/FA',
			procedure: {
				color: theme.applicationNumber.procedureFAColor,
				backgroundColor: theme.applicationNumber.procedureFABackgroundColor,
				...borderColor(theme.applicationNumber.procedureFABorderColor),
			},
			language: {
				color: theme.applicationNumber.languageFAColor,
				backgroundColor: theme.applicationNumber.languageFABackgroundColor,
			},
		},
		FS: {
			id: 'FS',
			label: 'National Search/FS',
			procedure: {
				color: theme.applicationNumber.procedureFSColor,
				backgroundColor: theme.applicationNumber.procedureFSBackgroundColor,
				...borderColor(theme.applicationNumber.procedureFSBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageFSColor,
				backgroundColor: theme.applicationNumber.languageFSBackgroundColor,
			},
		},
		BO: {
			id: 'BO',
			label: 'National Search/BO',
			procedure: {
				color: theme.applicationNumber.procedureBOColor,
				backgroundColor: theme.applicationNumber.procedureBOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureBOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageBOColor,
				backgroundColor: theme.applicationNumber.languageBOBackgroundColor,
			},
		},
		GO: {
			id: 'GO',
			label: 'National Search/GO',
			procedure: {
				color: theme.applicationNumber.procedureGOColor,
				backgroundColor: theme.applicationNumber.procedureGOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureGOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageGOColor,
				backgroundColor: theme.applicationNumber.languageGOBackgroundColor,
			},
		},
		RO: {
			id: 'RO',
			label: 'National Search/RO',
			procedure: {
				color: theme.applicationNumber.procedureROColor,
				backgroundColor: theme.applicationNumber.procedureROBackgroundColor,
				...borderColor(theme.applicationNumber.procedureROBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageROColor,
				backgroundColor: theme.applicationNumber.languageROBackgroundColor,
			},
		},
		CO: {
			id: 'CD',
			label: 'National Search/CO',
			procedure: {
				color: theme.applicationNumber.procedureCDColor,
				backgroundColor: theme.applicationNumber.procedureCDBackgroundColor,
				...borderColor(theme.applicationNumber.procedureCOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageCOColor,
				backgroundColor: theme.applicationNumber.languageCOBackgroundColor,
			},
		},
		IO: {
			id: 'IO',
			label: 'National Search/IO',
			procedure: {
				color: theme.applicationNumber.procedureIOColor,
				backgroundColor: theme.applicationNumber.procedureIOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureIOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageIOColor,
				backgroundColor: theme.applicationNumber.languageIOBackgroundColor,
			},
		},
		MO: {
			id: 'MO',
			label: 'National Search/MO',
			procedure: {
				color: theme.applicationNumber.procedureMOColor,
				backgroundColor: theme.applicationNumber.procedureMOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureMOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageMOColor,
				backgroundColor: theme.applicationNumber.languageMOBackgroundColor,
			},
		},
		SN: {
			id: 'SN',
			label: 'National Search/SN',
			procedure: {
				color: theme.applicationNumber.procedureSNColor,
				backgroundColor: theme.applicationNumber.procedureSNBackgroundColor,
				...borderColor(theme.applicationNumber.procedureSNBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageSNColor,
				backgroundColor: theme.applicationNumber.languageSNBackgroundColor,
			},
		},
		LT: {
			id: 'LT',
			label: 'National Search/LT',
			procedure: {
				color: theme.applicationNumber.procedureLTColor,
				backgroundColor: theme.applicationNumber.procedureLTBackgroundColor,
				...borderColor(theme.applicationNumber.procedureLTBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageLTColor,
				backgroundColor: theme.applicationNumber.languageLTBackgroundColor,
			},
		},
		NO: {
			id: 'NO',
			label: 'National Search/NO',
			procedure: {
				color: theme.applicationNumber.procedureNOColor,
				backgroundColor: theme.applicationNumber.procedureNOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureNOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageNOColor,
				backgroundColor: theme.applicationNumber.languageNOBackgroundColor,
			},
		},
		TO: {
			id: 'TO',
			label: 'National Search/TO',
			procedure: {
				color: theme.applicationNumber.procedureTOColor,
				backgroundColor: theme.applicationNumber.procedureTOBackgroundColor,
				...borderColor(theme.applicationNumber.procedureTOBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageTOColor,
				backgroundColor: theme.applicationNumber.languageTOBackgroundColor,
			},
		},
		DEFAULT: {
			id: 'default',
			label: 'National Search/default',
			procedure: {
				color: theme.applicationNumber.procedureDefaultColor,
				backgroundColor: theme.applicationNumber.procedureDefaultBackgroundColor,
				...borderColor(theme.applicationNumber.procedureDefaultBorderColor),
			},
			language: {
				color: theme.applicationNumber.languageDefaultColor,
				backgroundColor: theme.applicationNumber.languageDefaultBackgroundColor,
			},
		},
	};

	return applicationNumberMap[procedureCode];
};
